import {merge} from '#core/utils/config-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateEmptyContentMessage} from '#core/application/modules/tiles/empty-content-message/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';

export function eventPageFactory(PALETTE) {
    const {FONT} = PALETTE;
    const VISUALISATION_SELECTOR = {
        ...PALETTE.EVENT_PAGE_VISUALISATION_SELECTOR,
        TOGGLE_BUTTON_BORDER_RADIUS: 0, // because we cannot normally hide corners if radius is not 0
        TOGGLE_BUTTON_TEXT: {
            textTransform: 'capitalize',
            fontWeight: FONT.WEIGHT_NORMAL,
            ...PALETTE.EVENT_PAGE_VISUALISATION_SELECTOR.TOGGLE_BUTTON_TEXT,
        },
        TOGGLE_BUTTON_SELECTED_TEXT: {
            textTransform: 'capitalize',
            fontWeight: FONT.WEIGHT_NORMAL,
            ...PALETTE.EVENT_PAGE_VISUALISATION_SELECTOR.TOGGLE_BUTTON_SELECTED_TEXT,
        },
    };

    return {
        EventPage: {
            visualisationContainer: {
                style: {
                    ...resolveBackground(PALETTE.EVENT_PAGE_VISUALISATION_CONTAINER_BG),
                    _mobile_boxShadow: PALETTE.EVENT_PAGE_VISUALISATION_CONTAINER_SHADOW,
                },
            },
            pageHeaderConfig: {
                labelContainer: {
                    style: {
                        flex: 'initial',
                    },
                },
            },
            emptyContent: generateEmptyContentMessage(PALETTE.EVENT_PAGE_NO_CONTENT),
        },
        // because this component doesn't have nested config
        EventVisualisationController: {
            container: {
                itemSpacing: 0,
            },
            toggleMenuContainer: {
                itemSpacing: 4,
                style: {
                    padding: 4,
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderRadius: 6,
                    borderColor: PALETTE.EVENT_PAGE_VISUALISATION_SELECTOR_CONTAINER_BORDER_COLOR,
                    boxSizing: 'border-box',
                },
            },
            toggleButton: merge(generateToggleButton(VISUALISATION_SELECTOR, true), {
                unselected: {
                    style: {
                        base: {
                            minHeight: '100%',
                            height: 'initial',
                            margin: 0,
                            borderColor: 'transparent',
                        },
                        hover: {
                            borderColor: 'transparent',
                            ...resolveBackground(VISUALISATION_SELECTOR.TOGGLE_BUTTON_BG),
                            ...VISUALISATION_SELECTOR.TOGGLE_BUTTON_TEXT,
                        },
                        active: {
                            borderColor: 'transparent',
                            ...resolveBackground(VISUALISATION_SELECTOR.TOGGLE_BUTTON_BG),
                            ...VISUALISATION_SELECTOR.TOGGLE_BUTTON_TEXT,
                        },
                    },
                },
                selected: {
                    style: {
                        base: {
                            minHeight: '100%',
                            height: 'initial',
                            margin: 0,
                            borderColor: 'transparent',
                            borderRadius: 4,
                        },
                        hover: {
                            borderColor: 'transparent',
                        },
                        active: {
                            borderColor: 'transparent',
                        },
                    },
                },
            }),
        },
        EventVisualisationSkeleton: {
            container: {
                style: {
                    ...resolveBackground(PALETTE.EVENT_PAGE_VISUALISATION_CONTAINER_BG),
                },
            },
            scoreboardSkeletonPart: {
                style: generateSkeletonPart(PALETTE.EVENT_PAGE_VISUALISATION_SKELETON_PART),
            },
            marketGroupCategoriesMenuContainer: {
                style: {
                    padding: 8,
                },
            },
            marketGroupCategoriesMenuItem: {
                style: generateSkeletonPart(PALETTE.EVENT_PAGE_VISUALISATION_SKELETON_PART),
            },
        },
        // Event markets switcher
        EventMarketsGroupSwitcher: {
            categoryContainer: {
                style: {
                    ...resolveBackground(PALETTE.MARKETS_CATEGORIES_CONTAINER_BG),
                },
            },
            categorySelector: merge(generatePivotsRow(PALETTE.MARKETS_CATEGORIES_SELECTOR), {
                buttonScrollPane: {
                    contentContainer: {
                        style: {
                            _mobile_padding: 0,
                        },
                    },
                },
            }),
            groupSelector: {
                marketsMenu: {
                    item: generateToggleButton(PALETTE.MARKETS_GROUPS_SELECTOR_BUTTON, true),
                },
                buttonBar: {
                    container: {
                        style: {
                            padding: 0,
                        },
                    },
                    button: generateToggleButton(PALETTE.MARKETS_GROUPS_SELECTOR_BUTTON, true),
                    style: {
                        newFeature: {
                            ...resolveBackground(PALETTE.MARKETS_GROUPS_SELECTOR_BUTTON_NEW_FEATURE_LABEL_BG),
                            borderRadius: PALETTE.MARKETS_GROUPS_SELECTOR_BUTTON_NEW_FEATURE_LABEL_BORDER_RADIUS,
                            fontSize: 10,
                            fontWeight: FONT.WEIGHT_NORMAL,
                            lineHeight: '16px',
                            bottom: '-7px',
                            right: '-4px',
                            ...PALETTE.MARKETS_GROUPS_SELECTOR_BUTTON_NEW_FEATURE_LABEL_TEXT,
                        },
                    },
                    expandButtonText: {
                        style: {
                            fontWeight: FONT.WEIGHT_SEMI_BOLD,
                            fontSize: FONT.SIZE_MEDIUM,
                            ...PALETTE.MARKETS_GROUPS_MORE_BUTTON_TEXT,
                        },
                    },
                    expandButtonIcon: {
                        image: {
                            style: {
                                fill: PALETTE.MARKETS_GROUPS_MORE_BUTTON_ICON_COLOR,
                            },
                        },
                    },
                },
            },
        },
        MarketGroupSelectorSkeleton: {
            container: {
                style: {
                    _mobile_padding: '8px 8px 0 8px',
                },
            },
        },
    };
}
